import dayjs from 'dayjs';
import { handleRequest } from "../../axios";
import { axiosInstance } from "./JsdhNotifikaceService";
import {
    ADMIN_ODPOJENI_NASTAVENI_DRUH_URL,
    ADMIN_ODPOJENI_NASTAVENI_URL,
    ADMIN_ODPOJENI_TICHY_REZIM_URL,
    ADMIN_ODPOJENI_VYPADKY_URL,
} from "../url";

export type DruhOdpojeni = 'KIOSEK' | 'RPI_OBJEKTU' | 'SHELLY';

export interface OdpojeniNastaveni {
    druh: DruhOdpojeni;
    zapnuto: boolean;
    prodlevaMin: number;
    prodlevaSpiciHod: number | null;
    oznamitObnoveni: boolean;
    zmenil: string | null;
    zmeneno: string | null;
    prijemci: number[];
}

export interface OdpojeniNastaveniRequest {
    zapnuto: boolean;
    prodlevaMin: number;
    prodlevaSpiciHod: number | null;
    oznamitObnoveni: boolean;
    prijemci: number[];
}

export interface OdpojeniVypadek {
    id: number;
    druh: DruhOdpojeni;
    zarizeniKlic: string;
    nazev: string | null;
    objektId: number | null;
    offlineOd: string;
    oznamenoOffline: string | null;
    obnoveno: string | null;
    oznamenoObnoveni: string | null;
    tichy: boolean;
    pricina: 'OFFLINE' | 'NEUKAZUJE_APLIKACI';
    pokusyOffline: number;
    pokusyObnoveni: number;
    zarizeniOffline: number | null;
    zarizeniOfflineCelkem: number | null;
    uzavreni: 'OBNOVENO' | 'POTLACENO' | null;
}

/** Společný tichý režim (spec 5.4): časy HH:mm v Europe/Prague, oba null = vypnuto. */
export interface OdpojeniTichyRezim {
    tichyOd: string | null;
    tichyDo: string | null;
    zmenil: string | null;
    zmeneno: string | null;
}

export interface OdpojeniTichyRezimRequest {
    tichyOd: string | null;
    tichyDo: string | null;
}

export const TEXT_TICHY_REZIM =
    'V tichém režimu upozornění na odpojení nechodí. Zařízení, které je offline i po jeho konci, se oznámí hned potom.';

export const DRUHY: DruhOdpojeni[] = ['KIOSEK', 'RPI_OBJEKTU', 'SHELLY'];

export const NAZEV_DRUHU: Record<DruhOdpojeni, string> = {
    KIOSEK: 'Kiosek',
    RPI_OBJEKTU: 'RPi v objektu',
    SHELLY: 'Shelly',
};

export const POPIS_DRUHU: Record<DruhOdpojeni, string> = {
    KIOSEK: 'Displej ve zbrojnici a jeho kiosková stránka.',
    RPI_OBJEKTU: 'Raspberry Pi přiřazené objektu jako Zigbee brána.',
    SHELLY: 'Zásuvky, spínače a detektory Shelly v evidenci zařízení.',
};

/** Spec oddíl 3: kiosek 15–1440 min (metriky chodí po 5 min), ostatní 5–1440 min. */
const ROZSAH_IOT = { min: 5, max: 1440 };
export const ROZSAH_PRODLEVY: Record<DruhOdpojeni, { min: number; max: number }> = {
    KIOSEK: { min: 15, max: 1440 },
    RPI_OBJEKTU: ROZSAH_IOT,
    SHELLY: ROZSAH_IOT,
};

export const ROZSAH_SPICI_HOD = { min: 1, max: 720 };

export const TEXT_BEZ_PRIJEMCU: Record<DruhOdpojeni, string> = {
    KIOSEK: 'Když nikoho nevyberete, upozornění nedostane nikdo.',
    RPI_OBJEKTU: 'Když nikoho nevyberete, upozornění dostanou IoT příjemci objektu.',
    SHELLY: 'Když nikoho nevyberete, upozornění dostanou IoT příjemci objektu.',
};

/** Sloupec Upozornění v tabulce výpadků (spec oddíl 7). */
export const stavUpozorneni = (v: OdpojeniVypadek): string => {
    if (v.tichy) {
        return 'při startu';
    }
    if (v.oznamenoOffline) {
        // 0 z 0: příjemci nemají platný token, upozornění se bere jako vyřízené, ale nikomu nedorazilo
        if (v.zarizeniOfflineCelkem === 0) {
            return 'bez příjemců';
        }
        return v.zarizeniOfflineCelkem ? `odesláno ${v.zarizeniOffline ?? 0} z ${v.zarizeniOfflineCelkem}` : 'odesláno';
    }
    return 'neodesláno';
};

/** Čas z BE pro tabulku výpadků a kartu nastavení, např. "5. 10. 2026 8:05". */
export const formatCas = (s: string): string => dayjs(s).format('D. M. YYYY H:mm');

/** "45 min", "1 h", "2 h 15 min"; trvající výpadek do `ted`. Časy z BE jsou bez zóny = místní čas. */
export const delkaVypadku = (od: string, doCasu: string | null, ted: Date = new Date()): string => {
    const konec = doCasu ? new Date(doCasu) : ted;
    const min = Math.max(0, Math.floor((konec.getTime() - new Date(od).getTime()) / 60000));
    if (min < 60) {
        return `${min} min`;
    }
    const h = Math.floor(min / 60);
    const zbytek = min % 60;
    return zbytek === 0 ? `${h} h` : `${h} h ${zbytek} min`;
};

const OdpojeniService = {
    nastaveni: () => handleRequest<OdpojeniNastaveni[]>(() => axiosInstance.get(ADMIN_ODPOJENI_NASTAVENI_URL)),
    uloz: (druh: DruhOdpojeni, data: OdpojeniNastaveniRequest) =>
        handleRequest<OdpojeniNastaveni>(() => axiosInstance.put(ADMIN_ODPOJENI_NASTAVENI_DRUH_URL(druh), data)),
    tichyRezim: () => handleRequest<OdpojeniTichyRezim>(() => axiosInstance.get(ADMIN_ODPOJENI_TICHY_REZIM_URL)),
    ulozTichyRezim: (data: OdpojeniTichyRezimRequest) =>
        handleRequest<OdpojeniTichyRezim>(() => axiosInstance.put(ADMIN_ODPOJENI_TICHY_REZIM_URL, data)),
    vypadky: (druh?: DruhOdpojeni, limit = 50) =>
        handleRequest<OdpojeniVypadek[]>(() => axiosInstance.get(ADMIN_ODPOJENI_VYPADKY_URL,
            { params: { druh: druh || undefined, limit } })),
};

export default OdpojeniService;
